/**
 * Supervisor's dispatch board — day view with drag-drop reassignment.
 *
 * People × hour grid. Tasks render as bars positioned by scheduled time,
 * colored by status (ADR-0010), with a persistent Unassigned lane at the
 * bottom. Drag a task bar from one crew row to another (or to/from the
 * Unassigned lane) to reassign. Wireframe:
 * docs/wireframes/03-supervisor-dispatch-board.html.
 *
 * Bars position by each crew member's assignment-block window (ADR-0013) and
 * a per-crew conflict flag warns on overlapping blocks. Week / 2-week views
 * and keyboard shortcuts ship later.
 */
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { addDays, format, parseISO } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, Truck } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import type {
  DispatchTask,
  DispatchDayResponse,
  DispatchPickupBlock,
  DispatchProjectBlock,
} from '@vari-crew/schemas';
import { useDispatchDay, useNeedsScheduling, useReassignBlock } from '@/api/dispatch';
import { todayLocalISODate } from '@/lib/dates';
import { useAssignVehicle, useReassignTask } from '@/api/tasks';
import { useVehicles } from '@/api/vehicles';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { TaskBar } from '@/components/dispatch/TaskBar';
import { BlockBar } from '@/components/dispatch/BlockBar';
import { AssignCrewDialog } from '@/components/projects/AssignCrewButton';
import { cn } from '@/lib/cn';

// Hour grid runs 6am to 7pm = 13 hours
const HOUR_START = 6;
const HOUR_END = 19;
const HOURS = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);

// One column per quarter-hour for finer positioning
const COLS_PER_HOUR = 4;
const TOTAL_COLS = HOURS.length * COLS_PER_HOUR;

const UNASSIGNED_DROP_ID = 'unassigned';
type DragKind = 'task' | 'project' | 'pickup';
function crewDropId(crewMemberId: string): string {
  return `crew:${crewMemberId}`;
}
function dragId(kind: DragKind, entityId: string, fromCrewMemberId: string | null): string {
  return `${kind}:${entityId}:${fromCrewMemberId ?? UNASSIGNED_DROP_ID}`;
}
function parseDragId(id: string): { kind: DragKind; entityId: string; fromCrewMemberId: string | null } {
  const [kind = 'task', entityId = '', fromKey = ''] = id.split(':');
  return {
    kind: kind as DragKind,
    entityId,
    fromCrewMemberId: fromKey === UNASSIGNED_DROP_ID ? null : fromKey,
  };
}
function parseDropId(id: string): { toCrewMemberId: string | null } {
  if (id === UNASSIGNED_DROP_ID) return { toCrewMemberId: null };
  const [, crewMemberId = ''] = id.split(':');
  return { toCrewMemberId: crewMemberId };
}

/**
 * Translate a Date's local h:mm into the grid column index (0-based,
 * inclusive of HOUR_START).
 */
function dateToColumn(d: Date | null): number | null {
  if (!d) return null;
  const local = new Date(d);
  const hour = local.getHours();
  const min = local.getMinutes();
  const quarters = (hour - HOUR_START) * COLS_PER_HOUR + Math.round(min / 15);
  if (quarters < 0) return 0;
  if (quarters >= TOTAL_COLS) return TOTAL_COLS - 1;
  return quarters;
}

function barPositionStyle(
  startIso: string | null,
  endIso: string | null,
): React.CSSProperties | undefined {
  const start = startIso ? new Date(startIso) : null;
  const end = endIso ? new Date(endIso) : null;
  const startCol = dateToColumn(start);
  const endCol = dateToColumn(end);
  if (startCol == null || endCol == null) return undefined;
  const span = Math.max(1, endCol - startCol);
  return {
    gridColumnStart: startCol + 2, // +1 for crew column, +1 for 1-based
    gridColumnEnd: `span ${span}`,
  };
}

/**
 * Draggable wrapper around TaskBar. The drag activation distance of 6px
 * keeps clicks-as-Link working — only sustained drags trigger reassignment.
 */
function DraggableTaskBar({
  task,
  fromCrewMemberId,
  unassigned = false,
  blockStart = null,
  blockEnd = null,
  onOpen,
}: {
  task: DispatchTask;
  fromCrewMemberId: string | null;
  unassigned?: boolean;
  blockStart?: string | null;
  blockEnd?: string | null;
  /** Click (not drag) opens the assign-crew picker for this task. */
  onOpen?: () => void;
}) {
  const id = dragId('task', task.id, fromCrewMemberId);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
  });
  // Position by the crew member's block window (ADR-0013); fall back to the
  // task window for unassigned bars + not-yet-scheduled blocks.
  const positionStyle = barPositionStyle(
    blockStart ?? task.scheduled_start,
    blockEnd ?? task.scheduled_end,
  );
  const style: React.CSSProperties = {
    ...positionStyle,
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.75 : undefined,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onOpen?.()}
    >
      <TaskBar task={task} unassigned={unassigned} />
    </div>
  );
}

/** Draggable PROJECT block bar (D-3) — drag between crew reassigns the project. */
function DraggableProjectBar({
  block,
  fromCrewMemberId,
  onOpen,
}: {
  block: DispatchProjectBlock;
  fromCrewMemberId: string;
  onOpen?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId('project', block.project_id, fromCrewMemberId),
  });
  const style: React.CSSProperties = {
    ...barPositionStyle(block.scheduled_start, block.scheduled_end),
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.75 : undefined,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} onClick={() => onOpen?.()}>
      <BlockBar
        kind="project"
        title={block.project_title}
        subtitle={block.client_name}
        status={block.status}
        isLead={block.is_lead}
      />
    </div>
  );
}

/** Draggable PICKUP block bar (workstream C) — drag between crew reassigns it. */
function DraggablePickupBar({
  block,
  fromCrewMemberId,
  onOpen,
}: {
  block: DispatchPickupBlock;
  fromCrewMemberId: string;
  onOpen?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId('pickup', block.pickup_id, fromCrewMemberId),
  });
  const style: React.CSSProperties = {
    ...barPositionStyle(block.scheduled_start, block.scheduled_end),
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.75 : undefined,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  const orders = `${block.project_count} ${block.project_count === 1 ? 'order' : 'orders'}`;
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} onClick={() => onOpen?.()}>
      <BlockBar kind="pickup" title={block.title} subtitle={orders} status={block.status} isLead={block.is_lead} />
    </div>
  );
}

export function DispatchBoard() {
  // The viewed day lives in the URL (?date=) so a hard refresh keeps the day
  // instead of snapping back to today (§H).
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || todayLocalISODate();
  const setDate = (next: string) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('date', next);
        return p;
      },
      { replace: true },
    );
  };
  const { data, isLoading, error } = useDispatchDay(date);
  const { data: needsScheduling } = useNeedsScheduling();
  const reassign = useReassignTask();
  const reassignBlock = useReassignBlock();

  // Click a bar → open the assign-crew picker; drag → reassign. `draggingRef`
  // swallows the click that fires right after a drag-drop so it doesn't open
  // the picker on top of a reassignment.
  const draggingRef = useRef(false);
  const [pickerTaskId, setPickerTaskId] = useState<string | null>(null);
  function openTaskPicker(taskId: string) {
    if (draggingRef.current) return;
    setPickerTaskId(taskId);
  }
  const pickerTask = pickerTaskId ? data?.tasks_by_id[pickerTaskId] : null;

  const sensors = useSensors(
    // 6px activation distance — distinguishes a click (navigates) from a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Once the timeline is scrolled right, collapse the sticky crew column down
  // to just the initials avatar — on a phone the 220px name column otherwise
  // covers a third of the screen. setState no-ops until the threshold flips.
  const [crewCollapsed, setCrewCollapsed] = useState(false);

  const dateObj = parseISO(date);
  const isToday = date === todayLocalISODate();

  const unassignedTasks = useMemo(() => {
    if (!data) return [];
    return data.unassigned_task_ids
      .map((id) => data.tasks_by_id[id])
      .filter(Boolean) as DispatchTask[];
  }, [data]);

  function handleDragEnd(e: DragEndEvent) {
    if (!e.over) return;
    const { kind, entityId, fromCrewMemberId } = parseDragId(String(e.active.id));
    const { toCrewMemberId } = parseDropId(String(e.over.id));
    if (fromCrewMemberId === toCrewMemberId) return;
    const label =
      toCrewMemberId === null ? 'Sent to Unassigned' : fromCrewMemberId === null ? 'Assigned' : 'Reassigned';
    const opts = {
      onSuccess: () => toast.success(label),
      onError: (err: Error) => toast.error(`Reassign failed: ${err.message}`),
    };
    if (kind === 'task') {
      reassign.mutate(
        { taskId: entityId, body: { from_crew_member_id: fromCrewMemberId, to_crew_member_id: toCrewMemberId } },
        opts,
      );
    } else {
      // Project / pickup blocks (A6-2) — reassign via the block endpoints.
      reassignBlock.mutate({ kind, id: entityId, from: fromCrewMemberId, to: toCrewMemberId }, opts);
    }
  }

  return (
    <div className="p-5 max-w-[1600px] mx-auto space-y-3">
      {/* ---------- Top bar ---------- */}
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Dispatch board</h1>
          <p className="text-sm text-ink-dim">
            Click a bar to assign crew · drag to move between crew · color by status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setDate(format(addDays(dateObj, -1), 'yyyy-MM-dd'))}
            aria-label="Previous day"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-medium px-3 py-2 border border-line rounded-md min-w-[180px] text-center">
            {format(dateObj, 'EEE · MMM d, yyyy')}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setDate(format(addDays(dateObj, 1), 'yyyy-MM-dd'))}
            aria-label="Next day"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => setDate(todayLocalISODate())}>
              Today
            </Button>
          )}
        </div>
      </header>

      {/* ---------- Needs-scheduling safety net (§D) ---------- */}
      {needsScheduling && needsScheduling.tasks.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {needsScheduling.tasks.length} committed task
            {needsScheduling.tasks.length > 1 ? 's' : ''} need scheduling
          </div>
          <p className="text-[12px] text-amber-800 mt-0.5">
            These belong to committed projects but have no time yet, so they are not on the board.
            Open the project to schedule them.
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {needsScheduling.tasks.slice(0, 12).map((t) => (
              <li key={t.task_id}>
                <Link
                  to={`/projects/${t.project_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white/60 px-2 py-0.5 text-xs text-amber-900 hover:bg-white"
                >
                  {t.project_title}
                  <span className="text-amber-600">· {t.task_title}</span>
                </Link>
              </li>
            ))}
            {needsScheduling.tasks.length > 12 && (
              <li className="self-center text-xs text-amber-800">
                +{needsScheduling.tasks.length - 12} more
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Committed projects with nobody assigned (D-3/A6) — projects are
          schedulable directly now, so a crewless one would otherwise be
          invisible. Open the project to assign crew. */}
      {needsScheduling && needsScheduling.projects.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {needsScheduling.projects.length} committed project
            {needsScheduling.projects.length > 1 ? 's' : ''} need crew
          </div>
          <p className="text-[12px] text-amber-800 mt-0.5">
            These are committed but have nobody assigned yet. Open the project to assign crew.
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {needsScheduling.projects.slice(0, 12).map((p) => (
              <li key={p.project_id}>
                <Link
                  to={`/projects/${p.project_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white/60 px-2 py-0.5 text-xs text-amber-900 hover:bg-white"
                >
                  {p.project_title}
                  <span className="text-amber-600">· {p.client_name}</span>
                </Link>
              </li>
            ))}
            {needsScheduling.projects.length > 12 && (
              <li className="self-center text-xs text-amber-800">
                +{needsScheduling.projects.length - 12} more
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Live pickups with nobody on them (workstream C) — open the pickup to
          assign crew. Same idea as the crewless-projects banner. */}
      {needsScheduling && needsScheduling.pickups.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {needsScheduling.pickups.length} pickup
            {needsScheduling.pickups.length > 1 ? 's need' : ' needs'} crew
          </div>
          <p className="text-[12px] text-amber-800 mt-0.5">
            Warehouse runs with nobody assigned yet. Open the pickup to assign crew.
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {needsScheduling.pickups.slice(0, 12).map((p) => (
              <li key={p.pickup_id}>
                <Link
                  to={`/pickups/${p.pickup_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white/60 px-2 py-0.5 text-xs text-amber-900 hover:bg-white"
                >
                  {p.display_title}
                  <span className="text-amber-600">
                    · {p.project_count} {p.project_count === 1 ? 'order' : 'orders'}
                  </span>
                </Link>
              </li>
            ))}
            {needsScheduling.pickups.length > 12 && (
              <li className="self-center text-xs text-amber-800">
                +{needsScheduling.pickups.length - 12} more
              </li>
            )}
          </ul>
        </div>
      )}

      {/* ---------- Capacity header ---------- */}
      {data && (
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <span className="text-ink-faint uppercase tracking-wide">Capacity</span>
          <div className="flex items-center gap-2">
            <CapacityBar pct={data.capacity.capacity_used_pct} />
            <span className="font-mono text-ink">
              {data.capacity.total_assigned_hours}h
              <span className="text-ink-faint"> / {data.capacity.total_available_hours}h</span>
              <span
                className={cn(
                  'ml-1 font-semibold',
                  data.capacity.capacity_used_pct > 100
                    ? 'text-status-blocked-fg'
                    : 'text-ink-dim',
                )}
              >
                {data.capacity.capacity_used_pct}%
              </span>
            </span>
          </div>
          {data.capacity.unassigned_count > 0 && (
            <span className="px-2 py-0.5 rounded bg-status-scheduled text-status-scheduled-fg">
              {data.capacity.unassigned_count} unassigned
            </span>
          )}
          {data.capacity.blocked_count > 0 && (
            <span className="px-2 py-0.5 rounded bg-status-blocked text-status-blocked-fg">
              {data.capacity.blocked_count} blocked
            </span>
          )}
          {data.capacity.conflict_count > 0 && (
            <span className="px-2 py-0.5 rounded bg-status-blocked text-status-blocked-fg">
              ⚠ {data.capacity.conflict_count} conflict{data.capacity.conflict_count === 1 ? '' : 's'}
            </span>
          )}
          {data.capacity.vehicle_conflict_count > 0 && (
            <span className="px-2 py-0.5 rounded bg-status-blocked text-status-blocked-fg">
              ⚠ {data.capacity.vehicle_conflict_count} vehicle double-book
              {data.capacity.vehicle_conflict_count === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {/* ---------- Loading / error ---------- */}
      {isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-ink-faint" />
          </CardContent>
        </Card>
      )}
      {error && (
        <Card>
          <CardContent className="py-12 text-center text-status-blocked-fg text-sm">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {/* ---------- Grid ---------- */}
      {data && (
        <Card>
          <DndContext
            sensors={sensors}
            onDragStart={() => {
              draggingRef.current = true;
            }}
            onDragEnd={(e) => {
              handleDragEnd(e);
              window.setTimeout(() => {
                draggingRef.current = false;
              }, 0);
            }}
            onDragCancel={() => {
              window.setTimeout(() => {
                draggingRef.current = false;
              }, 0);
            }}
          >
            <div
              className="overflow-x-auto"
              onScroll={(e) => setCrewCollapsed(e.currentTarget.scrollLeft > 24)}
              // Snap to hour columns, and inset the snap by the sticky crew
              // column's width so the leftmost hour lands just past it (fully
              // visible) instead of sliding behind it as you scroll.
              style={{
                scrollSnapType: 'x proximity',
                scrollPaddingLeft: crewCollapsed ? 56 : 220,
              }}
            >
              <div
                className="grid gap-y-1 min-w-[1100px]"
                style={{
                  gridTemplateColumns: `${crewCollapsed ? 56 : 220}px repeat(${TOTAL_COLS}, minmax(8px, 1fr))`,
                  transition: 'grid-template-columns 200ms ease',
                }}
              >
                {/* Header row */}
                <div
                  className={cn(
                    'border-b border-line bg-fill-soft sticky left-0 z-30 py-2 text-[10px] uppercase tracking-wide text-ink-faint',
                    crewCollapsed ? 'px-0 text-center' : 'px-3',
                  )}
                >
                  Crew
                </div>
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="border-b border-line bg-fill-soft text-[10px] text-ink-faint py-2 border-l border-line/60"
                    style={{ gridColumn: `span ${COLS_PER_HOUR}`, scrollSnapAlign: 'start' }}
                  >
                    <span className="pl-1">
                      {h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`}
                    </span>
                  </div>
                ))}

                {/* Crew rows */}
                {data.crew_rows.map((row) => (
                  <CrewLane
                    key={row.crew_member.id}
                    row={row}
                    tasksById={data.tasks_by_id}
                    onOpenTask={openTaskPicker}
                    collapsed={crewCollapsed}
                  />
                ))}

                {/* Unassigned row */}
                <UnassignedLane
                  tasks={unassignedTasks}
                  onOpenTask={openTaskPicker}
                  collapsed={crewCollapsed}
                />
              </div>
            </div>
          </DndContext>

          {/* Legend — TASK statuses (what the bars actually are), not the
              project pipeline: 'Requested'/'Pending closeout' never render as
              bars and only confused the read. */}
          <div className="border-t border-line px-4 py-3 flex items-center gap-2 flex-wrap text-xs">
            <span className="text-ink-faint">Legend:</span>
            {(
              [
                ['scheduled', 'Scheduled', 'bg-status-scheduled text-status-scheduled-fg'],
                ['en_route', 'En route', 'bg-status-enroute text-status-enroute-fg'],
                ['in_progress', 'On site / working', 'bg-status-onsite text-status-onsite-fg'],
                ['complete', 'Complete', 'bg-status-complete text-status-complete-fg'],
                ['blocked', 'Blocked', 'bg-status-blocked text-status-blocked-fg'],
              ] as const
            ).map(([key, label, cls]) => (
              <span
                key={key}
                className={`inline-flex items-center rounded px-1.5 py-0.5 font-medium ${cls}`}
              >
                {label}
              </span>
            ))}
            <span className="ml-auto text-ink-faint">
              Click a bar to assign crew · drag to move between crew
            </span>
          </div>
        </Card>
      )}

      {data && <VehiclePanel data={data} />}

      {pickerTask && (
        <AssignCrewDialog
          task={{
            id: pickerTask.id,
            title: pickerTask.title,
            scheduled_start: pickerTask.scheduled_start,
            scheduled_end: pickerTask.scheduled_end,
            project_id: pickerTask.project_id,
            crew_size_needed: pickerTask.crew_size_needed,
            crew_roles_needed: pickerTask.crew_roles_needed,
          }}
          day={date}
          onClose={() => setPickerTaskId(null)}
        />
      )}
    </div>
  );
}

function VehiclePanel({ data }: { data: DispatchDayResponse }) {
  const { data: vehiclesData } = useVehicles();
  const assign = useAssignVehicle();
  const vehicles = vehiclesData?.data ?? [];
  // Only 'active' vehicles are bookable; out-of-service (in the shop) / retired
  // ones stay off the picker. A vehicle already booked on a task that's since
  // gone unavailable still shows (as a disabled-looking option) so it's visible.
  const bookable = vehicles.filter((v) => v.status === 'active');
  const conflictIds = new Set(data.vehicle_conflict_task_ids);

  const tasks = Object.values(data.tasks_by_id)
    .filter((t) => t.scheduled_start)
    .sort((a, b) => (a.scheduled_start! < b.scheduled_start! ? -1 : 1));

  if (tasks.length === 0) return null;

  const time = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        })
      : '—';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Truck className="w-4 h-4" /> Vehicles today
        </CardTitle>
        {data.capacity.vehicle_conflict_count > 0 && (
          <span className="text-xs px-2 py-0.5 rounded bg-status-blocked text-status-blocked-fg">
            ⚠ {data.capacity.vehicle_conflict_count} double-booked
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="border-y border-line bg-fill-soft text-xs uppercase tracking-wide text-ink-faint">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Task</th>
              <th className="px-4 py-2 text-left font-medium">Time</th>
              <th className="px-4 py-2 text-left font-medium">Vehicle</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => {
              const conflict = conflictIds.has(t.id);
              // If the booked vehicle is retired (not in the active list), keep
              // it selectable so it still shows.
              const missingAssigned =
                t.vehicle_id && !bookable.some((v) => v.id === t.vehicle_id);
              return (
                <tr
                  key={t.id}
                  className={cn(
                    'border-b border-line last:border-0',
                    conflict && 'bg-amber-50',
                  )}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium truncate">{t.title}</div>
                    <div className="text-xs text-ink-faint truncate">
                      {t.project_client_name}
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap tabular-nums text-ink-dim">
                    {time(t.scheduled_start)}–{time(t.scheduled_end)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={t.vehicle_id ?? ''}
                        disabled={assign.isPending}
                        onChange={(e) =>
                          assign.mutate(
                            { taskId: t.id, vehicleId: e.target.value || null },
                            {
                              onError: (err) =>
                                toast.error(`Failed: ${err.message}`),
                            },
                          )
                        }
                        className={cn(
                          'h-8 rounded border bg-bg px-2 text-sm',
                          conflict ? 'border-amber-400' : 'border-line',
                        )}
                      >
                        <option value="">— none —</option>
                        {missingAssigned && (
                          <option value={t.vehicle_id!}>
                            {t.vehicle_label ?? 'assigned'} (unavailable)
                          </option>
                        )}
                        {bookable.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                      {conflict && (
                        <span className="text-[10px] text-amber-700 whitespace-nowrap">
                          double-booked
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/** Avatar ring + text color for the day load band — kept visible (with the %)
 *  when the crew column collapses, so the capacity read survives. */
function loadBand(pct: number, hasTasks: boolean): { ring: string; text: string } {
  if (!hasTasks || pct === 0) return { ring: 'ring-line', text: 'text-ink-faint' };
  if (pct > 100) return { ring: 'ring-status-blocked-fg', text: 'text-status-blocked-fg' };
  if (pct > 80) return { ring: 'ring-status-enroute-fg', text: 'text-status-enroute-fg' };
  return { ring: 'ring-status-complete-fg', text: 'text-status-complete-fg' };
}

function CrewLane({
  row,
  tasksById,
  onOpenTask,
  collapsed,
}: {
  row: DispatchDayResponse['crew_rows'][number];
  tasksById: DispatchDayResponse['tasks_by_id'];
  onOpenTask: (taskId: string) => void;
  collapsed: boolean;
}) {
  const navigate = useNavigate();
  const name = row.crew_member.user.display_name;
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const isOver = row.assigned_hours > row.capacity_hours;
  const warn = isOver || row.has_conflict;
  const pct = row.capacity_hours > 0 ? Math.round((row.assigned_hours / row.capacity_hours) * 100) : 0;
  // Conflicts read as red regardless of how full the day is.
  const band = row.has_conflict
    ? { ring: 'ring-status-blocked-fg', text: 'text-status-blocked-fg' }
    : loadBand(pct, row.assigned_hours > 0);
  const { setNodeRef, isOver: isDragOver } = useDroppable({
    id: crewDropId(row.crew_member.id),
  });

  return (
    <>
      <div
        className={cn(
          'sticky left-0 z-20 py-2 text-xs font-medium border-t border-line bg-bg',
          collapsed ? 'px-1' : 'px-3',
        )}
      >
        <div className={cn('flex items-center gap-2', collapsed && 'justify-center')}>
          <div className="flex flex-col items-center gap-0.5 shrink-0">
            <div
              title={
                collapsed
                  ? `${name} · ${row.crew_member.crew_role} · ${row.assigned_hours}h/${row.capacity_hours}h (${pct}%)${row.has_conflict ? ' · ⚠ conflict' : ''}`
                  : undefined
              }
              className={cn(
                'w-7 h-7 rounded-full bg-fill-soft flex items-center justify-center text-[10px] font-bold shrink-0',
                collapsed ? `ring-2 ${band.ring}` : warn && 'ring-2 ring-status-blocked-fg',
              )}
            >
              {initials}
            </div>
            {collapsed && (
              <span className={cn('text-[9px] font-mono leading-none', band.text)}>{pct}%</span>
            )}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="truncate">{name}</div>
              <div className={cn('text-[10px]', isOver ? 'text-status-blocked-fg' : 'text-ink-faint')}>
                {row.crew_member.crew_role} · {row.assigned_hours}h / {row.capacity_hours}h
              </div>
              {row.has_conflict && (
                <div
                  className="text-[10px] font-semibold text-status-blocked-fg"
                  title="Overlapping assignment blocks — double-booked"
                >
                  ⚠ schedule conflict
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'relative grid gap-1 border-t border-line bg-bg transition-colors',
          isDragOver && 'bg-status-scheduled',
        )}
        style={{
          gridColumn: `2 / span ${TOTAL_COLS}`,
          gridTemplateColumns: `repeat(${TOTAL_COLS}, minmax(8px, 1fr))`,
          minHeight: '48px',
          alignItems: 'center',
          paddingBlock: '4px',
        }}
      >
        {/* Hour gridlines (visual only) */}
        {HOURS.map((h, i) => (
          <div
            key={h}
            className="border-l border-line/40 h-full"
            style={{ gridColumn: `${i * COLS_PER_HOUR + 1} / span ${COLS_PER_HOUR}` }}
          />
        ))}
        {row.assignments.map((a) => {
          const t = tasksById[a.task_id];
          if (!t) return null;
          return (
            <DraggableTaskBar
              key={`${row.crew_member.id}:${a.task_id}`}
              task={t}
              fromCrewMemberId={row.crew_member.id}
              blockStart={a.scheduled_start}
              blockEnd={a.scheduled_end}
              onOpen={() => onOpenTask(t.id)}
            />
          );
        })}
        {/* Project blocks (D-3) + pickup blocks (C) the crew member holds that
            day — draggable to reassign; click opens the detail. */}
        {row.project_blocks.map((b) => (
          <DraggableProjectBar
            key={`${row.crew_member.id}:p:${b.project_id}`}
            block={b}
            fromCrewMemberId={row.crew_member.id}
            onOpen={() => navigate(`/projects/${b.project_id}`)}
          />
        ))}
        {row.pickup_blocks.map((b) => (
          <DraggablePickupBar
            key={`${row.crew_member.id}:pk:${b.pickup_id}`}
            block={b}
            fromCrewMemberId={row.crew_member.id}
            onOpen={() => navigate(`/pickups/${b.pickup_id}`)}
          />
        ))}
      </div>
    </>
  );
}

function UnassignedLane({
  tasks,
  onOpenTask,
  collapsed,
}: {
  tasks: DispatchTask[];
  onOpenTask: (taskId: string) => void;
  collapsed: boolean;
}) {
  const { setNodeRef, isOver: isDragOver } = useDroppable({ id: UNASSIGNED_DROP_ID });
  return (
    <>
      <div
        className={cn(
          'sticky left-0 z-20 py-2 text-xs font-medium border-t border-line',
          collapsed ? 'px-1' : 'px-3',
          tasks.length > 0
            ? 'bg-status-blocked text-status-blocked-fg'
            : 'bg-fill-soft text-ink-faint',
        )}
      >
        <div className={cn('flex items-center gap-2', collapsed && 'justify-center')}>
          <span
            title={collapsed ? `Unassigned · ${tasks.length === 0 ? 'all covered' : `${tasks.length} need crew`}` : undefined}
            className="w-7 h-7 rounded-full bg-bg text-status-blocked-fg flex items-center justify-center font-bold shrink-0"
          >
            ?
          </span>
          {!collapsed && (
            <div>
              <div>Unassigned</div>
              <div className="text-[10px] opacity-75">
                {tasks.length === 0 ? 'all covered' : `${tasks.length} need crew`}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'relative grid gap-1 border-t border-line transition-colors',
          isDragOver && 'ring-2 ring-status-blocked-fg/40',
        )}
        style={{
          gridColumn: `2 / span ${TOTAL_COLS}`,
          gridTemplateColumns: `repeat(${TOTAL_COLS}, minmax(8px, 1fr))`,
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent, transparent 6px, var(--color-fill-soft) 6px, var(--color-fill-soft) 12px)',
          minHeight: '48px',
          alignItems: 'center',
          paddingBlock: '4px',
        }}
      >
        {tasks.map((t) => (
          <DraggableTaskBar
            key={t.id}
            task={t}
            fromCrewMemberId={null}
            unassigned
            onOpen={() => onOpenTask(t.id)}
          />
        ))}
      </div>
    </>
  );
}

function CapacityBar({ pct }: { pct: number }) {
  const clamped = Math.min(pct, 150);
  return (
    <div className="w-32 h-2 bg-fill-soft rounded-full overflow-hidden relative">
      <div
        className={cn(
          'h-full transition-all',
          pct > 100 ? 'bg-status-blocked-fg' : pct > 80 ? 'bg-status-enroute-fg' : 'bg-status-complete-fg',
        )}
        style={{ width: `${(clamped / 150) * 100}%` }}
      />
    </div>
  );
}
